/**
 * SwipePage Component
 * 
 * A Tinder-like swipe interface for products that allows users to:
 * - Browse products one at a time
 * - Swipe right to like products
 * - Swipe left to dislike products
 * - Click the heart button to add to favorites
 * - Navigate through multiple product images
 */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Grid,
  Card,
  Typography,
  Box,
  CardMedia,
  IconButton
} from '@mui/material';
import { getProductsByCategory } from '../Func/Products';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CheckIcon from '@mui/icons-material/Check';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SEO from '../components/SEO';

export default function SwipePage() {
  /**
   * State Management
   */
  // Product data states
  const [products, setProducts] = useState([]); // All available products
  const [currentIndex, setCurrentIndex] = useState(null); // Index of current product
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // Index of current image for multi-image products
  
  // UI state
  const [loading, setLoading] = useState(true); // Controls loading indicator
  const [overlayColor, setOverlayColor] = useState(null); // Color overlay for feedback
  const [liked, setLiked] = useState(false); // Whether current product is liked
  const [showHeart, setShowHeart] = useState(false); // Controls heart animation
  
  // Swipe interaction states
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left' or 'right'
  const [swipeOffset, setSwipeOffset] = useState(0); // Current drag position
  const [isDragging, setIsDragging] = useState(false); // Whether card is being dragged
  const [isExiting, setIsExiting] = useState(false); // Whether card is animating out
  const [exitDirection, setExitDirection] = useState(null); // Direction card is exiting
  
  // Saved product collections
  const [likedSwipes, setLikedSwipes] = useState([]); // Products swiped right
  const [dislikedSwipes, setDislikedSwipes] = useState([]); // Products swiped left
  
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const tutorialTimeoutRef = useRef(null);
  
  // Constants
  const ANIMATION_DURATION = 300; // ms
  const SITE_URL = 'https://holywearhouse.com';
  
  // Refs
  const startXRef = useRef(0); // Starting X position for swipe
  const startYRef = useRef(0); // Starting Y position for swipe

  /**
   * Effects
   */
  // Fetch products on component mount
  useEffect(() => {
    async function loadProducts() {
      try {
        const fetchedProducts = await getProductsByCategory("All Products");
        setProducts(fetchedProducts);
        setCurrentIndex(fetchedProducts.length - 1);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    }
    loadProducts();
  }, []);

  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [currentIndex]);

  // Check if current product is in liked items
  useEffect(() => {
    if (!products.length || currentIndex === null) return;

    // Preload images for current product
    const preloadImages = (product) => {
      if (product && Array.isArray(product.images)) {
        product.images.forEach(img =>
          new window.Image().src = typeof img === 'string' ? img : img.src
        );
      }
    };

    preloadImages(products[currentIndex]);
    if (currentIndex > 0) {
      preloadImages(products[currentIndex - 1]);
    }
  }, [products, currentIndex]);

  const swipeStructuredData = useMemo(() => {
    const itemList =
      products.length > 0
        ? {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Swipe Christian Clothing Finds | Holy Wear House',
            itemListElement: products.slice(0, 20).map((product, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: product.title,
              url: product.link || SITE_URL,
              image: product.images?.[0] || product.imageUrl,
              brand: product.brand,
            })),
          }
        : null;

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Swipe',
          item: `${SITE_URL}/swipe`,
        },
      ],
    };

    return [itemList, breadcrumb].filter(Boolean);
  }, [products, SITE_URL]);

  // Check if user has seen tutorial before
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('hasSeenSwipeTutorial');
    
    if (!hasSeenTutorial && !loading && products.length > 0) {
      // Wait for everything to load before showing tutorial
      setTimeout(() => {
        setShowTutorial(true);


        
        // Play tutorial animation sequence
        const animateTutorial = async () => {
          // Wait a moment before starting
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Animate right
          setSwipeOffset(80); // Move slightly right
          setSwipeDirection('right');
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // Back to center
          setSwipeOffset(0);
          setSwipeDirection(null);
          await new Promise(resolve => setTimeout(resolve, 400));
          
          // Animate left
          setSwipeOffset(-80); // Move slightly left
          setSwipeDirection('left');
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // Back to center
          setSwipeOffset(0);
          setSwipeDirection(null);
        };
        
        animateTutorial();
      }, 1000);
    }
    
    return () => {
      if (tutorialTimeoutRef.current) {
        clearTimeout(tutorialTimeoutRef.current);
      }
    };
  }, [loading, products]);

  /**
   * Helper Functions
   */
  /**
   * Adds an item to a localStorage array, preventing duplicates
   * @param {string} key - The localStorage key to store the array under
   * @param {Object} item - The item object to add to the array
   */
  function addToLocalStorageArray(key, item) {
    // Get existing array or initialize empty array if none exists
    const existing = JSON.parse(localStorage.getItem(key)) || [];
    
    // Check if item already exists by matching id or title to prevent duplicates
    const alreadyExists = existing.some(i => i.id === item.id || i.title === item.title);
    
    // Only add the item if it doesn't already exist
    if (!alreadyExists) {
      existing.push(item);
      localStorage.setItem(key, JSON.stringify(existing));
    }
  }
  
  /**
   * Event Handlers
   */
  /**
   * Handles the heart/favorite button click
   * Adds/removes the current product from the "likedItems" list
   * @param {Event} e - The click event
   */
  const handleFavorite = (e) => {
    e.stopPropagation(); 
    
    // Get current liked items from localStorage
    const likedItems = JSON.parse(localStorage.getItem('likedItems')) || [];
    
    if (liked) {
      // Remove from liked items - match by id or title
      const updated = likedItems.filter(
        item => (item.id !== product.id) && (item.title !== product.title)
      );
      localStorage.setItem('likedItems', JSON.stringify(updated));
      setLiked(false);
    } else {
      // Add to liked items - save whole object
      addToLocalStorageArray('likedItems', product);
      setLiked(true);
    }
    
    // Show heart animation briefly
    setShowHeart(true);
    setTimeout(() => setShowHeart(false), 300);
    
    // Move to next product with gold overlay color
    handleNext('rgba(255,215,0,0.7)'); 
  };

  /**
   * Handles right swipe (like) action
   * Adds current product to likedSwipes in localStorage and updates state
   */
  const handleLike = () => {
    setLikedSwipes(prev => {
      // Add to localStorage
      addToLocalStorageArray('likedSwipes', product);
      // Update state with new item
      return [...prev, product];
    });
    
    // Move to next product with green overlay color
    handleNext('rgba(0,255,0,0.7)');
  };

  /**
   * Handles left swipe (dislike) action
   * Adds current product to dislikedSwipes in localStorage and updates state
   */
  const handleDislike = () => {
    setDislikedSwipes(prev => {
      // Add to localStorage
      addToLocalStorageArray('dislikedSwipes', product);
      // Update state with new item
      return [...prev, product];
    });
    
    // Move to next product with red overlay color
    handleNext('rgba(255,0,0,0.7)');
  };

  /**
   * Advances to the next product
   * @param {string} color - The overlay color to show during transition
   */
  const handleNext = (color) => {
    // Show color overlay
    setOverlayColor(color);
    
    // Reset states for new card
    setIsExiting(false);
    setExitDirection(null);
    setSwipeOffset(0);
    setCurrentImageIndex(0);
    
    // After brief delay, advance to next product and clear overlay
    setTimeout(() => {
      setCurrentIndex(prevIndex => {
        // If we're at the last product, cycle back to first
        if (prevIndex >= products.length - 1) {
          return 0;
        }
        return prevIndex + 1;
      });
      setOverlayColor(null);
    }, 300);
  };

  /**
   * Touch and Mouse Event Handlers for Swiping
   */
  const handleSwipeStart = (clientX) => {
    setIsDragging(true);
    startXRef.current = clientX;
  };
  
  const handleSwipeMove = (clientX) => {
    if (!isDragging || !product) return;

    const deltaX = clientX - startXRef.current;
    setSwipeOffset(deltaX);

    // Set direction for color overlay
    if (deltaX > 50) {
      setSwipeDirection('right');
    } else if (deltaX < -50) {
      setSwipeDirection('left');
    }
  };
  const handleSwipeEnd = () => {
    if (!isDragging || !product) return;
    
    const SWIPE_THRESHOLD = 100;
    const EXIT_ANIMATION_DURATION = 300; // ms
    const NEXT_CARD_DELAY = 50; // ms delay before resetting position
    
    if (swipeOffset > SWIPE_THRESHOLD) {
      // Right swipe - like
      setIsExiting(true);
      setExitDirection('right');
      
      // Wait for most of the exit animation to complete before loading next card
      setTimeout(() => {
        handleLike(); // This should update currentIndex to next product
        
        // Wait a small amount of time for the new product to render
        setTimeout(() => {
          setIsExiting(false);
          setExitDirection(null);
          setSwipeOffset(0);
        }, NEXT_CARD_DELAY);
      }, EXIT_ANIMATION_DURATION - NEXT_CARD_DELAY);
      
    } else if (swipeOffset < -SWIPE_THRESHOLD) {
      // Left swipe - dislike
      setIsExiting(true);
      setExitDirection('left');
      
      setTimeout(() => {
        handleDislike(); // This should update currentIndex to next product
        
        setTimeout(() => {
          setIsExiting(false);
          setExitDirection(null);
          setSwipeOffset(0);
        }, NEXT_CARD_DELAY);
      }, EXIT_ANIMATION_DURATION - NEXT_CARD_DELAY);
      
    } else {
      // Not enough to trigger action, reset position
      setSwipeOffset(0);
    }
    
    setIsDragging(false);
    setSwipeDirection(null);
  };

  // Mouse events
  const handleMouseDown = (e) => handleSwipeStart(e.clientX);
  const handleMouseMove = (e) => handleSwipeMove(e.clientX);
  const handleMouseUp = () => handleSwipeEnd();

  // Touch events
  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
    startYRef.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !product) return;
    
    const deltaX = e.touches[0].clientX - startXRef.current;
    const deltaY = e.touches[0].clientY - startYRef.current;
    
    // Only prevent default and handle as a swipe if the movement
    // is primarily horizontal. A higher multiplier makes it less
    // likely to hijack a vertical scroll.
    if (Math.abs(deltaX) > Math.abs(deltaY) * 2) {
      e.preventDefault(); // Prevent scroll only on horizontal swipe
      setSwipeOffset(deltaX);
      
      if (deltaX > 50) {
        setSwipeDirection('right');
      } else if (deltaX < -50) {
        setSwipeDirection('left');
      }
    }
  };

  const handleTouchEnd = () => handleSwipeEnd();

  // Handle tutorial dismissal
  const handleDismissTutorial = () => {
    if (dontShowAgain) {
      localStorage.setItem('hasSeenSwipeTutorial', 'true');
    }
    setShowTutorial(false);
  };

  /**
   * Render Methods
   */
  // Loading state
  if (loading) {
    return <p>Loading products...</p>;
  }
  
  // Empty state
  if (products.length === 0) {
    return <Typography variant="h6" align="center">No more products.</Typography>;
  }
  
  // Get current product
  const product = products[currentIndex];
  
  // Add tutorial overlay when tutorial is active
  const renderTutorialOverlay = () => {
    if (!showTutorial) return null;
    
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Swipe to browse products
        </Typography>
        <Typography sx={{ mb: 4 }}>
          Swipe right to like, left to dislike
        </Typography>
        
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          
          <button
            onClick={handleDismissTutorial}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Got it
          </button>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 8 }}>
            <input
              type="checkbox"
              id="dontShowAgain"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
            <label htmlFor="dontShowAgain" style={{ marginLeft: '8px', fontSize: '14px' }}>
              Don't show me again
            </label>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      pb: 12,
      touchAction: 'pan-y', // Allow vertical scrolling
    }}>
      <SEO
        title="Swipe Christian Clothing Finds | Holy Wear House"
        description="Discover Christian streetwear one swipe at a time. Like, save, and explore faith-based apparel picks tailored for you."
        keywords="swipe christian clothing, discover christian apparel, holy wear house swipe"
        path="/swipe"
        structuredData={swipeStructuredData}
      />
      {renderTutorialOverlay()}
      <Box
        sx={{
          position: 'relative',
          maxWidth: '100%',
          width: '100%',
          // Remove fixed height or use min-height instead
          minHeight: '74vh',
          // Change to 'visible' or use 'auto' for overflow
          overflow: 'auto',
          borderRadius: '10px',
          touchAction: 'pan-y',
          transform: isExiting 
            ? exitDirection === 'right'
              ? 'translateX(150%) rotate(20deg)'
              : 'translateX(-150%) rotate(-20deg)'
            : `translateX(${swipeOffset}px) rotate(${swipeOffset * 0.05}deg)`,
          transition: isExiting
            ? `transform ${ANIMATION_DURATION}ms ease-out` 
            : isDragging 
              ? 'none' 
              : 'transform 0.3s ease',
        }}
        // Keep the event handlers
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Overlay for like/dislike indication */}
        {swipeDirection && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: swipeDirection === 'right' 
                ? 'rgba(0, 255, 0, 0.2)' 
                : 'rgba(255, 0, 0, 0.2)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {swipeDirection === 'right' ? (
              <CheckIcon sx={{ fontSize: 80, color: 'green' }} />
            ) : (
              <CloseIcon sx={{ fontSize: 80, color: 'red' }} />
            )}
          </Box>
        )}

        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{
            position: 'relative',
            width: '90vw',
            height: '57vh',
            margin: '0 auto'
          }}
        >
          <Card
            elevation={0}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '126%',
              boxShadow: 'none',
              border: 'none',
              zIndex: 1,
              overflow: 'hidden',
              borderRadius: '20px'
            }}
            onClick={() => setCurrentImageIndex((prev) =>
              prev + 1 < product.images.length ? prev + 1 : 0
            )}
          >
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              {/* Top gradient shade */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '80px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 100%)',
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />
              
              <CardMedia
                component="img"
                image={
                  product.images?.[currentImageIndex] ||
                  product.imageUrl ||
                  'https://via.placeholder.com/150'
                }
                alt={product.title}
                sx={{
                  width: '100%',
                  height: '100%',
                  cursor: product.images?.length > 1 ? 'pointer' : 'default'
                }}

              />
              
              {/* Heart button in bottom left */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  zIndex: 4,
                }}
              >
                <IconButton
                  onClick={(e) => handleFavorite(e)}
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <FavoriteBorderIcon sx={{ fontSize: 18, color: 'white' }} />
                  
                </IconButton>
              </Box>
              
              {/* If you have a bottom shade, it would be here */}
              
              {showHeart && (
                <FavoriteIcon
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    fontSize: 120,
                    color: 'rgba(233,30,99,0.85)',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    transition: 'opacity 0.3s'
                  }}
                />
              )}
            </Box>
            {/* Overlay */}
            {overlayColor && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: overlayColor,
                  opacity: 0.4,
                  zIndex: 2,
                  borderRadius: '20px',
                  pointerEvents: 'none',
                  transition: 'opacity 0.3s'
                }}
              />
            )}
            {product.images?.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 0,
                  right: 0,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-evenly', // Distributes items evenly
                  padding: '0 16px', // Add some padding on sides
                  zIndex: 5,
                  boxSizing: 'border-box',
                }}
              >
                {product.images.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      flex: 1, // Make each indicator grow to fill available space
                      height: '3px',
                      marginX: '2px', // Small gap between indicators
                      borderRadius: '1px',
                      backgroundColor: index === currentImageIndex 
                        ? 'white' 
                        : 'rgba(255, 255, 255, 0.5)',
                      transition: 'background-color 0.3s'
                    }}
                  />
                ))}
              </Box>
            )}
            <div
              style={{
                height: '100%',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '35%', // Increased from 20% to 35%
                  padding: '10px',
                  boxSizing: 'border-box',
                  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%)',
                  zIndex: 3,
                  borderBottomLeftRadius: '20px',
                  borderBottomRightRadius: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end' // Align content to bottom
                }}
              >
                <div
                  style={{
                    marginBottom: 'em'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    noWrap 
                    sx={{ 
                      color: 'white', 
                      fontWeight: 700, // Bold weight for title
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)' // Optional shadow for better readability
                    }}
                  >
                    {product.title}
                  </Typography>
                  <Typography 
                    variant="body3" 
                    noWrap 
                    sx={{ 
                      color: 'white', 
                      fontWeight: 600 // Semi-bold for brand
                    }}
                  >
                    {product.brand}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'white', 
                      fontWeight: 600, // Semi-bold for price
                      mt: 0.5 // Small margin top for spacing
                    }}
                  >
                    ${product.price}
                  </Typography>
                </div>
              </div>
            </div>
          </Card>
        </Grid>
      </Box>
    </Box>
  );
}
